import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import HeroAnimationController from "./HeroAnimationController.js";

class GrowthSmoothScroll {
    constructor(parent) {
        this.parent = parent;
        gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

        this.options = {
            sectionsContainer: '.full-height-sections',
            sectionClass: '.full-height-section',
            scrollDelay: 1,
            tolerance: 10,
            duration: 0.75,
        };

        this.state = {
            allowScroll: true,
            currentIndex: 0,
            isAnimating: false,
            lastPanelIndex: -1, // Track the last panel index for video reset
            hasAnchorNavigation: !!window.location.hash, // Flag to track if we're navigating to an anchor
            initialAnimationComplete: false,
            pendingAnchor: null, // Store the anchor to navigate to after initial animation
            isHandlingHashChange: false, // Flag to prevent hash update loops
        };

        // Panel to anchor mapping
        this.panelAnchors = {
            'compendium': 2,
            'latest': 3,
            'airevolution': 4
        };

        // Create reverse mapping of panel anchors for hash updates
        this.panelIndexToAnchor = {};
        for (const [anchor, index] of Object.entries(this.panelAnchors)) {
            this.panelIndexToAnchor[index] = anchor;
        }

        // Regular anchors that should use normal scrolling
        this.regularAnchors = ['portfolio', 'team'];

        this.init();
        this.setupAnchorNavigation();
        this.setupVideoReset();
    }

    /**
     * Initialize the smooth scroll functionality
     * @private
     */
    init() {
        this.swipePanels = gsap.utils.toArray(
            `${this.options.sectionsContainer} ${this.options.sectionClass}`
        );

        if (!this.swipePanels.length) {
            console.warn('No sections found with the specified selectors');
            return;
        }

        // Initialize scroll timeout
        this.scrollTimeout = gsap.delayedCall(
            this.options.scrollDelay,
            () => this.state.allowScroll = true
        ).pause();

        // Set up initial styles
        gsap.set(this.swipePanels, {
            zIndex: i => this.swipePanels.length - i,
        });

        this.animationController = new HeroAnimationController(this);

        this.initializeObserver();
        this.setupScrollTrigger();
        this.initialAnimation();
    }

    setupVideoReset() {
        this.videoResetTrigger = ScrollTrigger.create({
            trigger: "#main",
            start: 'top top',
            end: `+=${window.innerHeight}`,
            onLeave: () => {
                console.log('Main section ScrollTrigger onLeave - resetting video');
                this.resetVideoPlayer();
            }
        });
    }

    resetVideoPlayer() {
        if (this.parent && this.parent.videoPlayer) {
            this.parent.videoPlayer.resetVideoPlayer();
        }
    }

    initialAnimation() {
        this.timeline = this.animationController.createTimeline(0, true);
        
        // Setup callback for when initial animation completes
        this.timeline.eventCallback("onComplete", () => {
            this.state.initialAnimationComplete = true;
            this.state.currentIndex = 0; // Ensure we're at index 0 after initial animation
            console.log('Initial animation complete!');
            
            // If we have a pending anchor navigation, perform it now
            if (this.state.pendingAnchor) {
                console.log('Processing pending anchor navigation to:', this.state.pendingAnchor);
                const anchor = this.state.pendingAnchor;
                this.state.pendingAnchor = null;
                
                // Use a short delay when we have an anchor in the URL
                const anchorDelay = this.state.hasAnchorNavigation ? 10 : 50;
                
                // Use a small timeout to ensure state is updated before navigation
                setTimeout(() => {
                    this.navigateToAnchor(anchor);
                }, anchorDelay);
            }
        });
        
        // If we have an anchor in the URL, speed up the initial animation
        if (this.state.hasAnchorNavigation && this.timeline.duration) {
            console.log('Speeding up initial animation because of anchor in URL');
            this.timeline.timeScale(5); // 5x faster
        }
    }

    initializeObserver() {
        let lastEventTime = 0;
        const debounceTime = 300;
        const postAnimationDebounceTime = 1000;
        let accumulatedDelta = 0;
        const deltaThreshold = 100;
        const longSwipeThreshold = 300;

        const handleScroll = (direction, self) => {
            const now = Date.now();
            if (this.isCurrentlyAnimating() || now - lastEventTime < debounceTime) {
                console.log('Animation in progress or debouncing, ignoring scroll');
                return;
            }

            let isLongSwipe = false;
            if (self.deltaY) {
                const currentDelta = Math.abs(self.deltaY);
                accumulatedDelta += currentDelta;
                isLongSwipe = currentDelta > longSwipeThreshold;

                if (accumulatedDelta < deltaThreshold) {
                    return;
                }
            }

            lastEventTime = now;
            accumulatedDelta = 0;

            if (!this.state.allowScroll) return;

            if (direction === 'up') {
                if (this.state.currentIndex === 0) {
                    return;
                }
                this.gotoPanel(this.state.currentIndex - 1, false);
            } else {
                this.gotoPanel(this.state.currentIndex + 1, true);
            }

            const debounceDelay = isLongSwipe ? postAnimationDebounceTime : 0;
            setTimeout(() => {
                if (isLongSwipe) {
                    lastEventTime = Date.now();
                }
            }, this.options.duration * 1000 + debounceDelay);
        };

        this.intentObserver = ScrollTrigger.observe({
            type: "wheel,touch",
            onUp: (self) => handleScroll('up', self),
            onDown: (self) => handleScroll('down', self),
            tolerance: this.options.tolerance,
            preventDefault: true,
            onEnable: self => {
                this.state.allowScroll = false;
                this.scrollTimeout.restart(true);
                accumulatedDelta = 0;

                const savedScroll = self.scrollY();
                self._restoreScroll = () => self.scrollY(savedScroll);
                document.addEventListener("scroll", self._restoreScroll, { passive: false });
            },
            onDisable: self => {
                document.removeEventListener("scroll", self._restoreScroll);
            }
        });

        this.intentObserver.disable();
    }

    gotoPanel(index, isScrollingDown, fromHero = false) {
        console.log('Goto Panel', index, 'fromHero:', fromHero);

        if (index !== this.swipePanels.length) {
            console.log('Panel changed from', this.state.lastPanelIndex, 'to', index, '- resetting video');
            this.resetVideoPlayer();
        }

        // Return to normal scroll if we're at the end
        if ((index === this.swipePanels.length && isScrollingDown)) {
            this.intentObserver.disable();
            return;
        }

        // Prevent scrolling past the first panel
        if (index < 0) {
            return;
        }
        console.log('gotoPanel', index, this.state.isAnimating);
        // Prevent new animations while one is in progress
        if (this.state.isAnimating && !fromHero) {
            console.log('gotoPanel Animation in progress, ignoring new request');
            return;
        }

        this.state.allowScroll = false;
        console.log('is Animating !!!')
        this.state.isAnimating = true;
        this.scrollTimeout.restart(true);

        // Create the stage animation timeline with onComplete
        const stageTimeline = this.animationController.createTimeline(index, isScrollingDown);
        console.log('stageTimeline', stageTimeline);
        stageTimeline.eventCallback("onComplete", () => {
            this.state.currentIndex = index;
            this.state.isAnimating = false;
            console.log('gotoPanel onComplete isAnimating', this.state.isAnimating);
            
            // Update URL hash when animation completes
            this.updateURLHash(index);
            
            if(index === 0) {
                setTimeout(() => {
                    window.scrollTo(0, 0);
                }, 100)
            }
        });

        // Store the timeline reference
        this.timeline = stageTimeline;
    }

    // New method to update the URL hash based on panel index
    updateURLHash(index) {
        console.log('Updating URL hash for index:', index, 'isHandlingHashChange:', this.state.isHandlingHashChange);
        
        // Temporarily set the flag to prevent the hashchange event from firing
        // but only if we're actually going to change the hash
        const tempSetHandlingFlag = () => {
            this.state.isHandlingHashChange = true;
            // Reset the flag after a brief delay to allow future hash changes
            setTimeout(() => {
                console.log('Resetting isHandlingHashChange flag');
                this.state.isHandlingHashChange = false;
            }, 200);
        };
        
        // For index 0 (hero), remove the hash entirely
        if (index === 0) {
            if (window.location.hash) {
                console.log('Removing hash for hero section');
                tempSetHandlingFlag();
                history.pushState("", document.title, window.location.pathname + window.location.search);
            }
            return;
        }
        
        // For regular panels (1-4), update to the corresponding anchor
        if (index in this.panelIndexToAnchor) {
            const anchor = this.panelIndexToAnchor[index];
            const newHash = `#${anchor}`;
            
            // Only update if the hash has changed
            if (window.location.hash !== newHash) {
                console.log('Setting hash to:', newHash);
                tempSetHandlingFlag();
                window.location.hash = anchor;
            }
        }
    }

    // Add a method to check if animation is actually running
    isCurrentlyAnimating() {
        return this.timeline && this.timeline.isActive();
    }

    /**
     * Set up main scroll trigger
     * @private
     */
    setupScrollTrigger() {
        ScrollTrigger.create({
            trigger: this.options.sectionsContainer,
            pin: true,
            start: "top top",
            end: "+=200",
            onEnter: self => {
                if (this.intentObserver.isEnabled) return;
                self.scroll(self.start + 1);
                this.intentObserver.enable();
            },
            onEnterBack: self => {
                if (this.intentObserver.isEnabled) return;
                self.scroll(self.end - 1);
                this.intentObserver.enable();
            }
        });
        
        // Add scroll triggers for regular sections
        this.regularAnchors.forEach(anchor => {
            const element = document.getElementById(anchor);
            if (!element) return;
            
            ScrollTrigger.create({
                trigger: element,
                start: "top center",
                onEnter: () => {
                    if (!this.state.isHandlingHashChange) {
                        console.log(`Scrolled to regular section: ${anchor}`);
                        // Update hash when scrolling to this section
                        this.state.isHandlingHashChange = true;
                        window.location.hash = anchor;
                        
                        // Reset flag after delay
                        setTimeout(() => {
                            this.state.isHandlingHashChange = false;
                        }, 200);
                    }
                },
                onEnterBack: () => {
                    if (!this.state.isHandlingHashChange) {
                        console.log(`Scrolled back to regular section: ${anchor}`);
                        // Update hash when scrolling back to this section
                        this.state.isHandlingHashChange = true;
                        window.location.hash = anchor;
                        
                        // Reset flag after delay
                        setTimeout(() => {
                            this.state.isHandlingHashChange = false;
                        }, 200);
                    }
                }
            });
        });
    }

    /**
     * Go to specific panel
     * @param {number} index - Target panel index
     */
    goTo(index) {
        if (!this.state.allowScroll ||
            this.isCurrentlyAnimating() ||
            index === this.state.currentIndex ||
            index < 0 ||
            index >= this.swipePanels.length) {
            return;
        }

        this.gotoPanel(index, index > this.state.currentIndex);
    }

    setupAnchorNavigation() {
        // Handle clicks on navigation links
        document.querySelectorAll('.growth-page-nav a').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const href = link.getAttribute('href');
                if (!href) return;

                const anchor = href.replace('#', '');
                this.navigateToAnchor(anchor);
            });
        });

        // Handle direct hash changes in URL
        window.addEventListener('hashchange', () => {
            console.log('Hash change detected, isHandlingHashChange:', this.state.isHandlingHashChange);
            
            // Only handle if not already processing a hash change
            if (!this.state.isHandlingHashChange) {
                const hash = window.location.hash.replace('#', '');
                if (hash) {
                    this.navigateToAnchor(hash);
                } else if (this.state.currentIndex !== 0) {
                    // If hash is removed and we're not at the hero section, go there
                    this.goTo(0);
                }
            }
        });

        // Check initial hash on page load with a delay to ensure videoPlayer is properly initialized
        if (window.location.hash) {
            const hash = window.location.hash.replace('#', '');
            this.handleInitialHash(hash);
        }
    }

    handleInitialHash(hash) {
        // Don't interrupt intro animation, just queue the navigation
        if (hash) {
            if (!this.state.initialAnimationComplete) {
                console.log('Queuing anchor navigation to', hash, 'until after intro animation completes');
                this.state.pendingAnchor = hash;
                
                // If we have the instant intro method, process the anchor faster
                if (this.state.hasAnchorNavigation && this.timeline) {
                    console.log('Using fast intro for anchor navigation');
                    this.timeline.timeScale(10); // Make animation 10x faster to get to the anchor quickly
                }
            } else {
                // Initial animation already complete, navigate immediately
                setTimeout(() => {
                    this.navigateToAnchor(hash);
                }, 100);
            }
        }
    }

    navigateToAnchor(anchor) {
        console.log('goto anchor', anchor);
        
        // If initial animation isn't complete, queue this navigation
        if (!this.state.initialAnimationComplete) {
            console.log('Queuing anchor navigation until initial animation completes');
            this.state.pendingAnchor = anchor;
            return;
        }
        
        if (anchor in this.panelAnchors) {
            const panelIndex = this.panelAnchors[anchor];
            console.log('goto panel with index', panelIndex);

            // Kill any active animations (but not the initial one)
            if (this.isCurrentlyAnimating() && this.state.initialAnimationComplete) {
                console.log('Killing active animation to allow anchor navigation');
                this.timeline.kill();
                this.state.isAnimating = false;
            }

            // Only reset video players when navigating between panels (not for initial navigation)
            if (panelIndex !== this.state.currentIndex && 
                panelIndex !== this.swipePanels.length && 
                this.state.currentIndex !== 0) { // Avoid resetting on initial load
                console.log('Anchor navigation changing panel - resetting video');
                this.resetVideoPlayer();
            }

            // Get the actual from index (ensuring it's not the same as destination)
            const fromIndex = this.state.currentIndex;
            
            // Early return if we're already at the target panel
            if (fromIndex === panelIndex) {
                console.log('Already at target panel', panelIndex, '- no animation needed');
                return;
            }
            
            // Pre-set the destination for animation logic
            const isFromHero = fromIndex === 0;
            
            // Navigate to the panel - we don't need to set isHandlingHashChange here
            // as updateURLHash will handle it
            if (isFromHero && panelIndex > 1) {
                // Handle special case: going from hero (0) to any other panel
                console.log('Special case: animating from hero to panel', panelIndex);
                this.gotoPanel(panelIndex, true, true);
            } else {
                this.goTo(panelIndex);
            }
            
            if(panelIndex > 4) {
                gsap.to(window, {
                    duration: this.options.duration,
                    scrollTo: { y: 0, autoKill: false },
                    ease: "power2.inOut",
                });
            } else {
                window.scrollTo(0, 0);
            }
            return;
        }

        if (this.regularAnchors.includes(anchor)) {
            if (this.isCurrentlyAnimating()) {
                console.log('Animation in progress, ignoring anchor navigation');
                return;
            }

            console.log('goto regular anchor', anchor, 'from', this.state.currentIndex);

            this.intentObserver.disable();

            const element = document.getElementById(anchor);
            if (!element) {
                return;
            }

            const elementPosition = element.getBoundingClientRect().top + window.scrollY;

            // Update the URL hash for the regular section
            // Set the handling flag to prevent recursive hash updates
            const updateRegularHash = () => {
                console.log('Updating hash for regular section:', anchor);
                const newHash = `#${anchor}`;
                
                if (window.location.hash !== newHash) {
                    this.state.isHandlingHashChange = true;
                    window.location.hash = anchor;
                    
                    // Reset the flag after a delay
                    setTimeout(() => {
                        console.log('Resetting isHandlingHashChange flag for regular section');
                        this.state.isHandlingHashChange = false;
                    }, 200);
                }
            };

            this.state.isAnimating = true;
            const tl = gsap.timeline({
                onComplete: () => {
                    this.state.currentIndex = 5;
                    this.state.isAnimating = false;
                    console.log('goto regular anchor onComplete isAnimating', this.state.isAnimating);
                    
                    // Update the hash when animation completes
                    updateRegularHash();
                }
            });

            if (this.state.currentIndex < 4) {
                this.animationController.timelines.initialAnimateToLastPanel(tl, this.state.currentIndex);
            }

            tl.to(window, {
                duration: this.options.duration,
                scrollTo: { y: elementPosition - 80, autoKill: false },
                ease: "power2.inOut",
                onComplete: () => {
                    const timeline = gsap.timeline();
                    this.animationController.timelines.animateToLastPanel(timeline, this.state.currentIndex);
                }
            }, '-=0.5');
        }
    }

    /**
     * Clean up all event listeners and GSAP instances
     */
    destroy() {
        this.intentObserver?.disable();
        this.scrollTimeout?.kill();
        this.videoResetTrigger?.kill();
        ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        // Remove anchor navigation event listeners
        document.querySelectorAll('.growth-page-nav a').forEach(link => {
            link.removeEventListener('click');
        });
        window.removeEventListener('hashchange');
    }
}

export default GrowthSmoothScroll;
